/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/react";
import { forwardRef, type JSX } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { spacing } from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { KeyedElement } from "../../../../types/keyedElement.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import type { AvatarCommunityProps } from "../AvatarCommunity/AvatarCommunity.emotion";
import { AvatarCommunity } from "../AvatarCommunity/AvatarCommunity.emotion";
import { Text } from "../Text/Text.emotion";

const hallowElement: HallowElement = "div";

const determinedElementStyle: Interpolation = {
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
  width: spacing.full,
};
const infoStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.t,
  minWidth: spacing.none,
};
const detailsStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
});
const etAlStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  gap: spacing.ms,
  width: spacing.full,
};

export type HostProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  avatarProps?: AvatarCommunityProps;
  name?: string;
  description?: string;
  rightActions?: KeyedElement | KeyedElement[];
};

export const Host = forwardRef<any, HostProps>(
  (
    {
      asChild = false,
      avatarProps = {},
      description,
      children,
      name,
      rightActions,
      ...props
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const determinedChildren = determineChildrenInject({
      afterChildrenInject: [
        <Text type={"title"} size={"m"} as={"h3"}>
          <I18nMessage message={"community_hosted_by"} />
        </Text>,
        <div key={"host"} css={etAlStyle}>
          <AvatarCommunity key="avatar" size={"s"} {...avatarProps} />
          <div key="info" css={infoStyle}>
            <Text overflow="ellipsis" size={"m"} type="title">
              {name}
            </Text>
            <Text overflow="ellipsis" size={"s"} type="body" css={detailsStyle}>
              {description}
            </Text>
          </div>
        </div>,
        rightActions as JSX.Element,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement ref={ref} css={determinedElementStyle} {...props}>
        {determinedChildren}
      </DeterminedElement>
    );
  },
);

Host.displayName = "Host";
